/**
 * Patches a react-big-calendar localizer to handle DST transitions safely.
 *
 * The dayjsLocalizer's `add(date, 1, 'day')` uses `dayjs(date).add(1, 'day')`
 * which adds 24 wall-clock hours. During DST fall-back this can land on the
 * same calendar date twice, producing duplicate day cells in the month grid.
 *
 * The fix: jump forward 36 hours via raw UTC milliseconds (bypassing
 * browser-local Date methods entirely) then extract the date string and
 * reconstruct midnight using `dayjs.tz(dateString)`. This avoids the
 * dayjs `startOf('day')` bug where it preserves the UTC offset of the
 * input date, which can be wrong for midnight at DST spring-forward
 * boundaries (e.g. Australia/Melbourne October).
 *
 * @param {import("react-big-calendar").DateLocalizer} localizer
 * @param {import("dayjs").Dayjs} dayjsLib - the dayjs instance (with tz plugin)
 */
const THIRTY_SIX_HOURS_MS = 36 * 60 * 60 * 1000

export function patchLocalizerForDST(localizer, dayjsLib) {
  const origAdd = localizer.add.bind(localizer)
  // Use the timezone-aware constructor (dayjsLib.tz) so we resolve
  // midnight in the org timezone, not the browser's local timezone.
  // This mirrors what dayjsLocalizer does internally (see react-big-calendar
  // source: `var dayjs = dayjsLib.tz ? dayjsLib.tz : dayjsLib`).
  const djs = dayjsLib.tz || dayjsLib

  function dstSafeAdd(date, adder, unit) {
    if ((unit === "day" || unit === "d") && adder === 1) {
      // Extract the date string after jumping 36h, then reconstruct
      // midnight via dayjs.tz() which resolves the correct UTC offset
      // for that specific date (unlike startOf('day') which preserves
      // the input's offset).
      const nextDayStr = djs(date.valueOf() + THIRTY_SIX_HOURS_MS).format(
        "YYYY-MM-DD",
      )
      return djs(nextDayStr).toDate()
    }
    if (unit === "day" || unit === "d") {
      // For multi-day jumps, iterate one day at a time
      let current = date
      for (let i = 0; i < adder; i++) {
        current = dstSafeAdd(current, 1, "day")
      }
      return current
    }
    return origAdd(date, adder, unit)
  }

  localizer.add = dstSafeAdd

  // visibleDays and range close over the original add(), so we must
  // replace them to use dstSafeAdd instead.
  localizer.visibleDays = function visibleDays(date) {
    let current = localizer.firstVisibleDay(date)
    const last = localizer.lastVisibleDay(date)
    const days = []
    while (localizer.lte(current, last)) {
      days.push(current)
      current = dstSafeAdd(current, 1, "day")
    }
    return days
  }

  const origRange = localizer.range.bind(localizer)
  localizer.range = function range(start, end, unit = "day") {
    if (unit === "day" || unit === "d") {
      // Use the timezone-aware constructor to resolve midnight correctly at DST boundaries
      const startStr = djs(start).format("YYYY-MM-DD")
      let current = djs(startStr).toDate()
      const days = []
      while (localizer.lte(current, end)) {
        days.push(current)
        current = dstSafeAdd(current, 1, "day")
      }
      return days
    }
    return origRange(start, end, unit)
  }
}
